import React, { ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import './Patterns.css';

export interface TabPanelProps {
  /** Stable identifier matched against TabBar's activeKey. */
  id: string;
  /** Visible label rendered in the tablist button. */
  title: string;
  /** Panel content; rendered only when activeKey === id. */
  children: ReactNode;
}

/**
 * <TabPanel> declares one tab within a <TabBar>. It is a metadata-only
 * component — TabBar reads its props directly from React.Children. The
 * `displayName` is load-bearing: TabBar filters children by it.
 */
export const TabPanel: React.FC<TabPanelProps> = ({ children }) => <>{children}</>;
TabPanel.displayName = 'TabPanel';

export interface TabBarProps {
  /** id of the active TabPanel. */
  activeKey: string;
  /** Called with the new tab id on click or arrow-key navigation. */
  onSelect: (key: string) => void;
  /** TabPanel children. Non-TabPanel children are ignored. */
  children: ReactNode;
  /** Optional aria-label for the tablist; defaults to a translated string. */
  ariaLabel?: string;
}

/**
 * <TabBar> — pat-* tab primitive.
 *
 * Replacement for react-bootstrap <Tabs>/<Tab>. Renders a tablist of
 * <button role="tab"> elements and a single <div role="tabpanel"> for
 * the active panel. Switching tabs mounts/unmounts the panel content
 * **synchronously** in the same render cycle — no transition delay,
 * no fade — which avoids the breadcrumb-cleanup race condition that
 * forced `transition={false}` on the react-bootstrap Tabs in Phase C.
 *
 * ARIA: uses tablist / tab / tabpanel roles with aria-selected,
 * aria-controls, and aria-labelledby wired across. Supports
 * ArrowLeft / ArrowRight to cycle through tabs.
 *
 * Note: the `displayName === 'TabPanel'` filter (see TabPanel above) is
 * load-bearing. Wrapping a TabPanel inside another component (e.g.
 * `<MyTabSlot>` that renders `<TabPanel>`) will cause it to be skipped.
 * This matches the children-as-config pattern in react-bootstrap, Radix,
 * and Headless UI.
 */
const TabBar: React.FC<TabBarProps> = ({ activeKey, onSelect, children, ariaLabel }) => {
  const { t } = useTranslation();
  const idPrefix = useId();
  const tabs = React.Children.toArray(children).filter(
    (child): child is React.ReactElement<TabPanelProps> =>
      React.isValidElement(child) && (child.type as React.FC).displayName === 'TabPanel'
  );
  const activeTab = tabs.find((tab) => tab.props.id === activeKey) ?? tabs[0];

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, currentIdx: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = tabs[(currentIdx + 1) % tabs.length];
      onSelect(next.props.id);
      document.getElementById(`${idPrefix}-tab-${next.props.id}`)?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = tabs[(currentIdx - 1 + tabs.length) % tabs.length];
      onSelect(prev.props.id);
      document.getElementById(`${idPrefix}-tab-${prev.props.id}`)?.focus();
    }
  };

  return (
    <div className="pat-tab-bar" data-testid="tab-bar">
      <div
        className="pat-tab-bar__tablist"
        role="tablist"
        aria-label={ariaLabel ?? t('patterns.tabBar.tabListLabel')}
      >
        {tabs.map((tab, idx) => {
          const isActive = tab.props.id === activeKey;
          return (
            <button
              key={tab.props.id}
              type="button"
              role="tab"
              id={`${idPrefix}-tab-${tab.props.id}`}
              aria-selected={isActive}
              aria-controls={`${idPrefix}-panel-${tab.props.id}`}
              tabIndex={isActive ? 0 : -1}
              className={`pat-tab-bar__tab${isActive ? ' pat-tab-bar__tab--active' : ''}`}
              onClick={() => onSelect(tab.props.id)}
              onKeyDown={(e) => onKeyDown(e, idx)}
            >
              {tab.props.title}
            </button>
          );
        })}
      </div>
      {activeTab && (
        <div
          role="tabpanel"
          id={`${idPrefix}-panel-${activeTab.props.id}`}
          aria-labelledby={`${idPrefix}-tab-${activeTab.props.id}`}
          className="pat-tab-bar__panel"
        >
          {activeTab.props.children}
        </div>
      )}
    </div>
  );
};

export default TabBar;
